import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  AssociatedCampaignWithRelations,
  useGroupCreateCampaignsMutation,
  useSingleCreateCampaignMutation,
  useGetPlatformMappingsQuery,
  PlatformMapping,
} from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { resolveBriefingPlatformIcon } from "../../utils/iconUtils";
import { components } from "../../types/api";

type BriefingPlatform = components["schemas"]["BriefingPlatform"];
type CreationMode = "individual" | "grouped";

interface ProductionReviewDialogProps {
  open: boolean;
  briefingId: string;
  campaigns: AssociatedCampaignWithRelations[];
  onClose: () => void;
  onSuccess?: () => void;
}

interface AcReadiness {
  hasSourceId: boolean;
  hasGoal: boolean;
  hasTarget: boolean;
  ready: boolean;
}

function getReadiness(ac: AssociatedCampaignWithRelations): AcReadiness {
  const hasSourceId = Boolean(ac.sourceId?.trim());
  const hasGoal = ac.goal != null;
  const hasTarget = ac.target != null;
  return {
    hasSourceId,
    hasGoal,
    hasTarget,
    ready: hasSourceId && hasGoal && hasTarget,
  };
}

function getRealPlatformKey(
  platform: BriefingPlatform,
  mappings: PlatformMapping[]
): string {
  const mapping = mappings.find((m) => m.platform === platform);
  return mapping?.realPlatform ?? platform;
}

/** Small readiness badge: green check or red cross + label */
const ReadinessBadge: React.FC<{
  ok: boolean;
  label: string;
  tooltip: string;
}> = ({ ok, label, tooltip }) => {
  const theme = useTheme();
  return (
    <Tooltip title={tooltip} arrow>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="center"
        sx={{
          flex: 1,
          px: 1,
          py: 0.75,
          borderRadius: 1,
          border: `1px solid ${ok ? theme.palette.success.light : theme.palette.error.light}`,
          bgcolor: ok ? "success.50" : "error.50",
          cursor: "default",
          minWidth: 80,
        }}
      >
        {ok ? (
          <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
        ) : (
          <CancelIcon sx={{ fontSize: 16, color: "error.main" }} />
        )}
        <Typography
          variant="caption"
          fontWeight={600}
          color={ok ? "success.dark" : "error.dark"}
          noWrap
        >
          {label}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

const ProductionReviewDialog: React.FC<ProductionReviewDialogProps> = ({
  open,
  briefingId,
  campaigns,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [groupCreate] = useGroupCreateCampaignsMutation();
  const [singleCreate] = useSingleCreateCampaignMutation();

  // Per-group: which mode is selected (default: individual)
  const [groupModes, setGroupModes] = useState<Record<string, CreationMode>>(
    {}
  );
  // Per-group: loading state for "grouped" creation
  const [creatingGroup, setCreatingGroup] = useState<string | null>(null);
  // Per-group: done state for "grouped" creation
  const [createdGroups, setCreatedGroups] = useState<Set<string>>(new Set());
  // Per-AC: loading state for "individual" creation
  const [creatingAc, setCreatingAc] = useState<string | null>(null);
  // Per-AC: done state for "individual" creation
  const [createdAcs, setCreatedAcs] = useState<Set<string>>(new Set());

  const { data: mappingsData } = useGetPlatformMappingsQuery();
  const platformMappings: PlatformMapping[] = useMemo(
    () => mappingsData?.data ?? [],
    [mappingsData]
  );

  const groups = useMemo<Map<string, AssociatedCampaignWithRelations[]>>(() => {
    const map = new Map<string, AssociatedCampaignWithRelations[]>();
    for (const ac of campaigns) {
      if ((ac as { status?: string }).status === "READY") continue;
      // Also skip ACs that were individually created in this session
      if (createdAcs.has(ac.id)) continue;
      const key = getRealPlatformKey(ac.platform, platformMappings);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ac);
    }
    return map;
  }, [campaigns, platformMappings, createdAcs]);

  /** Get mode for a platform group, defaulting to "individual" */
  const getModeForGroup = (platformKey: string): CreationMode =>
    groupModes[platformKey] ?? "individual";

  const handleModeChange = (platformKey: string, newMode: CreationMode) => {
    setGroupModes((prev) => ({ ...prev, [platformKey]: newMode }));
  };

  /** Create all ACs in a group as a single grouped Campaign (existing behavior) */
  const handleCreateGroup = async (
    platformKey: string,
    groupAcs: AssociatedCampaignWithRelations[]
  ) => {
    setCreatingGroup(platformKey);
    try {
      await groupCreate({
        briefingId,
        associatedCampaignIds: groupAcs.map((ac) => ac.id),
      }).unwrap();
      setCreatedGroups((prev) => new Set([...prev, platformKey]));
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.groupCreateSuccess",
            "Campaigns created and associated campaigns linked successfully"
          ),
          severity: "success",
        })
      );
      onSuccess?.();
    } catch (err: unknown) {
      let msg = t(
        "briefingsView.groupCreateError",
        "Failed to create campaigns"
      );
      if (err && typeof err === "object" && "data" in err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errData = (err as any).data;
        if (errData) {
          if (Array.isArray(errData.errors) && errData.errors.length > 0) {
            msg = errData.errors
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((e: any) =>
                e.message
                  ? `${e.platform ? `[${e.platform}] ` : ""}${e.message}`
                  : JSON.stringify(e)
              )
              .join(", ");
          } else if (errData.message) {
            msg = errData.message;
          } else if (errData.error) {
            msg = errData.error;
          }
        }
      }
      dispatch(setToastMessage({ message: msg, severity: "error" }));
    } finally {
      setCreatingGroup(null);
    }
  };

  /** Create a single AC as its own individual Campaign (new behavior) */
  const handleCreateSingle = async (ac: AssociatedCampaignWithRelations) => {
    setCreatingAc(ac.id);
    try {
      await singleCreate({
        briefingId,
        associatedId: ac.id,
      }).unwrap();
      setCreatedAcs((prev) => new Set([...prev, ac.id]));
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.createSingleSuccess",
            "Campaign created and linked successfully"
          ),
          severity: "success",
        })
      );
      onSuccess?.();
    } catch (err: unknown) {
      let msg = t(
        "briefingsView.createSingleError",
        "Failed to create campaign"
      );
      if (err && typeof err === "object" && "data" in err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errData = (err as any).data;
        if (errData?.message) msg = errData.message;
      }
      dispatch(setToastMessage({ message: msg, severity: "error" }));
    } finally {
      setCreatingAc(null);
    }
  };

  const linkedCount = campaigns.filter(
    (ac) => (ac as { status?: string }).status === "READY"
  ).length;
  const allReady =
    linkedCount + createdAcs.size === campaigns.length &&
    [...createdGroups].every((gk) => (groups.get(gk) ?? []).length === 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        {t(
          "briefingsView.productionReviewTitle",
          "Create Campaigns — Production Review"
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {allReady ? (
          <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
            {t(
              "briefingsView.allReady",
              "All associated campaigns are ready. The briefing will be finalized."
            )}
          </Alert>
        ) : groups.size === 0 && createdAcs.size === 0 ? (
          <Alert severity="info">
            {t(
              "briefingsView.noGroupsToCreate",
              "No unlinked associated campaigns to process."
            )}
          </Alert>
        ) : (
          <Stack spacing={3}>
            {[...groups.entries()].map(([platformKey, groupAcs]) => {
              const mode = getModeForGroup(platformKey);
              const isGroupDone = createdGroups.has(platformKey);
              const isGroupCreating = creatingGroup === platformKey;
              const firstAc = groupAcs[0];
              const icon = resolveBriefingPlatformIcon(firstAc.platform);

              // In individual mode: all ACs in the current list are done if all are in createdAcs
              const allIndividuallyDone = groupAcs.every((ac) =>
                createdAcs.has(ac.id)
              );

              return (
                <Box
                  key={platformKey}
                  sx={{
                    border: `1px solid ${
                      isGroupDone || allIndividuallyDone
                        ? theme.palette.success.light
                        : theme.palette.grey[300]
                    }`,
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  {/* ── Group header ── */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      px: 3,
                      py: 2,
                      bgcolor:
                        isGroupDone || allIndividuallyDone
                          ? "success.50"
                          : "grey.50",
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        component="img"
                        src={icon.iconPath}
                        alt={platformKey}
                        sx={{ width: 28, height: 28 }}
                      />
                      <Typography variant="h6" fontWeight={700} fontSize={16}>
                        {platformKey}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${groupAcs.length} campaign${groupAcs.length !== 1 ? "s" : ""}`}
                        variant="outlined"
                      />
                    </Stack>

                    {isGroupDone || allIndividuallyDone ? (
                      <Chip
                        icon={<CheckCircleOutlineIcon />}
                        label={t("briefingsView.created", "Created")}
                        color="success"
                      />
                    ) : (
                      <Stack direction="row" spacing={2} alignItems="center">
                        {/* Mode toggle */}
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={mode}
                          onChange={(_e, val) => {
                            if (val) handleModeChange(platformKey, val);
                          }}
                          disabled={isGroupCreating || creatingAc !== null}
                        >
                          <ToggleButton value="individual" sx={{ px: 2 }}>
                            {t(
                              "briefingsView.createIndividually",
                              "Create individually"
                            )}
                          </ToggleButton>
                          <ToggleButton value="grouped" sx={{ px: 2 }}>
                            {t(
                              "briefingsView.createAsGroup",
                              "Create as group"
                            )}
                          </ToggleButton>
                        </ToggleButtonGroup>

                        {/* Grouped mode: single action button */}
                        {mode === "grouped" &&
                          (() => {
                            const allReady = groupAcs.every((ac) => {
                              const isReady = getReadiness(ac).ready;
                              const hasNativeAdProvider =
                                platformKey === "NATADS"
                                  ? Boolean(ac.nativeAdProvider)
                                  : true;
                              return isReady && hasNativeAdProvider;
                            });
                            return (
                              <Tooltip
                                title={
                                  !allReady
                                    ? platformKey === "NATADS"
                                      ? t(
                                          "briefingsView.notAllReadyNatads",
                                          "All campaigns must have Source ID, Goal, Target, and Native Ad Provider before creating"
                                        )
                                      : t(
                                          "briefingsView.notAllReady",
                                          "All campaigns must have Source ID, Goal, and Target before creating"
                                        )
                                    : ""
                                }
                              >
                                <span>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={
                                      !allReady ||
                                      isGroupCreating ||
                                      creatingGroup !== null ||
                                      creatingAc !== null
                                    }
                                    onClick={() =>
                                      handleCreateGroup(platformKey, groupAcs)
                                    }
                                    startIcon={
                                      isGroupCreating ? (
                                        <CircularProgress
                                          size={16}
                                          color="inherit"
                                        />
                                      ) : undefined
                                    }
                                    sx={{ minWidth: 160 }}
                                  >
                                    {isGroupCreating
                                      ? t("common.creating", "Creating...")
                                      : t(
                                          "briefingsView.createGroup",
                                          "Create campaigns"
                                        )}
                                  </Button>
                                </span>
                              </Tooltip>
                            );
                          })()}
                      </Stack>
                    )}
                  </Stack>

                  <Divider />

                  {/* ── Column headings ── */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                      px: 3,
                      py: 1,
                      bgcolor: "background.paper",
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ flex: 1 }}
                    >
                      {t("common.name", "Name")}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        width:
                          platformKey === "NATADS"
                            ? mode === "individual"
                              ? 500
                              : 400
                            : mode === "individual"
                              ? 400
                              : 300,
                      }}
                    >
                      {[
                        "Source ID",
                        t("briefingsView.goalLabel", "Goal"),
                        t("briefingsView.targetLabel", "Target"),
                        ...(platformKey === "NATADS"
                          ? [
                              t(
                                "briefingsView.nativeAdProvider",
                                "Native Ad Provider"
                              ),
                            ]
                          : []),
                        ...(mode === "individual" ? [""] : []),
                      ].map((col, i) => (
                        <Typography
                          key={`${col}-${i}`}
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ flex: 1, textAlign: "center" }}
                        >
                          {col}
                        </Typography>
                      ))}
                    </Stack>
                  </Stack>

                  {/* ── AC rows ── */}
                  {groupAcs.map((ac, idx) => {
                    const r = getReadiness(ac);
                    const isAcDone = createdAcs.has(ac.id);
                    const isThisCreating = creatingAc === ac.id;
                    const hasNativeAdProvider =
                      platformKey === "NATADS"
                        ? Boolean(ac.nativeAdProvider)
                        : true;
                    const acReady = r.ready && hasNativeAdProvider;

                    return (
                      <Stack
                        key={ac.id}
                        direction="row"
                        alignItems="center"
                        sx={{
                          px: 3,
                          py: 1.5,
                          bgcolor:
                            idx % 2 === 0 ? "background.paper" : "grey.50",
                          borderBottom:
                            idx < groupAcs.length - 1
                              ? `1px solid ${theme.palette.divider}`
                              : "none",
                          opacity: isAcDone ? 0.6 : 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ flex: 1 }}
                        >
                          {ac.name}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            width:
                              platformKey === "NATADS"
                                ? mode === "individual"
                                  ? 500
                                  : 400
                                : mode === "individual"
                                  ? 400
                                  : 300,
                          }}
                        >
                          <ReadinessBadge
                            ok={r.hasSourceId}
                            label="Source ID"
                            tooltip={
                              r.hasSourceId
                                ? `ID: ${ac.sourceId}`
                                : t(
                                    "briefingsView.missingSourceId",
                                    "Missing Source ID"
                                  )
                            }
                          />
                          <ReadinessBadge
                            ok={r.hasGoal}
                            label={t("briefingsView.goalLabel", "Goal")}
                            tooltip={
                              r.hasGoal
                                ? t("briefingsView.goalPresent", "Goal filled")
                                : t("briefingsView.missingGoal", "Missing Goal")
                            }
                          />
                          <ReadinessBadge
                            ok={r.hasTarget}
                            label={t("briefingsView.targetLabel", "Target")}
                            tooltip={
                              r.hasTarget
                                ? t(
                                    "briefingsView.targetPresent",
                                    "Target filled"
                                  )
                                : t(
                                    "briefingsView.missingTarget",
                                    "Missing Target"
                                  )
                            }
                          />
                          {platformKey === "NATADS" && (
                            <ReadinessBadge
                              ok={Boolean(ac.nativeAdProvider)}
                              label={t(
                                "briefingsView.providerLabel",
                                "Provider"
                              )}
                              tooltip={
                                ac.nativeAdProvider
                                  ? `Provider: ${ac.nativeAdProvider}`
                                  : t(
                                      "briefingsView.missingProvider",
                                      "Missing Native Ad Provider"
                                    )
                              }
                            />
                          )}

                          {/* Per-AC create button — only in individual mode */}
                          {mode === "individual" && (
                            <Box
                              sx={{
                                flex: 1,
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              {isAcDone ? (
                                <Chip
                                  icon={<CheckCircleOutlineIcon />}
                                  label={t("briefingsView.created", "Created")}
                                  color="success"
                                  size="small"
                                />
                              ) : (
                                <Tooltip
                                  title={
                                    !acReady
                                      ? platformKey === "NATADS"
                                        ? t(
                                            "briefingsView.notAllReadyNatads",
                                            "All campaigns must have Source ID, Goal, Target, and Native Ad Provider before creating"
                                          )
                                        : t(
                                            "briefingsView.notAllReady",
                                            "All campaigns must have Source ID, Goal, and Target before creating"
                                          )
                                      : ""
                                  }
                                >
                                  <span>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      disabled={
                                        !acReady ||
                                        isThisCreating ||
                                        creatingAc !== null ||
                                        creatingGroup !== null
                                      }
                                      onClick={() => handleCreateSingle(ac)}
                                      startIcon={
                                        isThisCreating ? (
                                          <CircularProgress
                                            size={12}
                                            color="inherit"
                                          />
                                        ) : undefined
                                      }
                                    >
                                      {isThisCreating
                                        ? t(
                                            "briefingsView.creatingIndividual",
                                            "Creating..."
                                          )
                                        : t("common.create", "Create")}
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </Stack>
                      </Stack>
                    );
                  })}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {t("common.close", "Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductionReviewDialog;
